import React, { useEffect, useState } from "react";
import { Button, Checkbox, DatePicker, Radio, Select, Tooltip } from "antd";
import { UndoOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { analyticsApi } from "../../../services/api";
import { APP_TRACKING_START, compareRangeFor, dayCount, formatRange, previousRange } from "../gaCompare";

const { RangePicker } = DatePicker;

// Values must match server/controllers/analytics/gaFilter.js. "Mobile browser" is a web segment
// and is never labelled bare "Mobile", so it can't be confused with the app.
export const WEB_SEGMENTS = [
  { value: "web_desktop", label: "Desktop" },
  { value: "web_tablet", label: "Tablet" },
  { value: "web_mobile", label: "Mobile browser" },
];
export const APP_SEGMENTS = [
  { value: "app_ios", label: "iOS" },
  { value: "app_android", label: "Android" },
];
export const ALL_SEGMENT_VALUES = [...WEB_SEGMENTS, ...APP_SEGMENTS].map((s) => s.value);

export const DEFAULT_GA_FILTER = {
  tenantMode: "include",
  tenants: [],
  platforms: ALL_SEGMENT_VALUES,
  hideUntagged: true,
  compareMode: "previous",
  compareStart: null,
};

export function defaultRange() {
  return [dayjs().subtract(29, "day"), dayjs()];
}

const sameRange = (a, b) => a[0].isSame(b[0], "day") && a[1].isSame(b[1], "day");
const sameDay = (a, b) => (a && b ? a.isSame(b, "day") : a === b);

function isSameFilter(a, b) {
  return (
    a.tenantMode === b.tenantMode &&
    a.hideUntagged === b.hideUntagged &&
    a.tenants.join(",") === b.tenants.join(",") &&
    a.platforms.join(",") === b.platforms.join(",") &&
    sameRange(a.range, b.range) &&
    a.compareMode === b.compareMode &&
    sameDay(a.compareStart, b.compareStart)
  );
}

function groupSummary(title, group, platforms) {
  const selected = group.filter((s) => platforms.includes(s.value));
  if (selected.length === group.length) return null;
  if (selected.length === 0) return `${title}: none`;
  return `${title}: ${selected.map((s) => s.label).join(", ")}`;
}

function summarize({ tenantMode, tenants, platforms, hideUntagged }) {
  const parts = [];
  if (tenants.length > 0) {
    const verb = tenantMode === "include" ? "Including" : "Excluding";
    parts.push(`${verb} ${tenants.length} tenant${tenants.length === 1 ? "" : "s"}`);
  }
  const web = groupSummary("Web", WEB_SEGMENTS, platforms);
  const app = groupSummary("App", APP_SEGMENTS, platforms);
  if (web) parts.push(web);
  if (app) parts.push(app);
  if (!hideUntagged) parts.push("incl. untagged traffic");
  return parts.length > 0 ? parts.join(" · ") : "All tenants · all platforms";
}

function tenantOptionLabel(t) {
  return t.subdomain && t.subdomain.toLowerCase() !== t.name.toLowerCase() ? `${t.name} (${t.subdomain})` : t.name;
}

// value: { tenantMode, tenants, platforms, hideUntagged, range, compareMode, compareStart }. Edits
// stay in a local draft until Apply, so the dashboard reloads once per deliberate change rather
// than on every click. The compare row only shows on the Compare tab (showCompare).
export default function GaFilterBar({ value, onApply, showCompare = false }) {
  const [draft, setDraft] = useState(value);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);

  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    analyticsApi
      .getTenants()
      .then((res) => setTenantOptions(res.data?.tenants || []))
      .catch(() => setTenantOptions([]))
      .finally(() => setTenantsLoading(false));
  }, []);

  const dirty = !isSameFilter(draft, value);

  function setPlatforms(platforms) {
    if (platforms.length === 0) return;
    setDraft({ ...draft, platforms: ALL_SEGMENT_VALUES.filter((v) => platforms.includes(v)) });
  }

  function toggleSegment(segment, checked) {
    setPlatforms(checked ? [...draft.platforms, segment] : draft.platforms.filter((s) => s !== segment));
  }

  function toggleGroup(group, allChecked) {
    const values = group.map((s) => s.value);
    setPlatforms(allChecked ? draft.platforms.filter((s) => !values.includes(s)) : [...draft.platforms, ...values]);
  }

  function reset() {
    setDraft({ ...DEFAULT_GA_FILTER, range: draft.range, compareMode: draft.compareMode, compareStart: draft.compareStart });
  }

  const days = dayCount(draft.range);
  const compareRange = compareRangeFor(draft);
  const appSelected = draft.platforms.some((p) => APP_SEGMENTS.some((s) => s.value === p));
  const noAppDataEarlier = appSelected && compareRange[0].isBefore(APP_TRACKING_START, "day");

  function setCompareMode(compareMode) {
    const compareStart = compareMode === "custom" ? draft.compareStart || previousRange(draft.range)[0] : draft.compareStart;
    setDraft({ ...draft, compareMode, compareStart });
  }

  const onlyOneLeft = draft.platforms.length === 1;

  const renderGroup = (title, group) => {
    const allChecked = group.every((s) => draft.platforms.includes(s.value));
    const groupIsAllThatsLeft = draft.platforms.every((p) => group.some((s) => s.value === p));
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="mini-label w-16">{title}</span>
        {group.map((s) => {
          const checked = draft.platforms.includes(s.value);
          const locked = checked && onlyOneLeft;
          return (
            <Tooltip key={s.value} title={locked ? "At least one platform must stay selected" : undefined}>
              <Checkbox checked={checked} disabled={locked} onChange={(e) => toggleSegment(s.value, e.target.checked)}>
                {s.label}
              </Checkbox>
            </Tooltip>
          );
        })}
        <Button
          size="small"
          type={allChecked ? "primary" : "default"}
          disabled={allChecked && groupIsAllThatsLeft}
          onClick={() => toggleGroup(group, allChecked)}
        >
          All
        </Button>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="mini-label w-16">Tenants</span>
        <Radio.Group
          size="small"
          optionType="button"
          value={draft.tenantMode}
          onChange={(e) => setDraft({ ...draft, tenantMode: e.target.value })}
          options={[
            { label: "Include", value: "include" },
            { label: "Exclude", value: "exclude" },
          ]}
        />
        <Select
          mode="multiple"
          allowClear
          loading={tenantsLoading}
          placeholder="All tenants"
          className="w-full sm:w-auto sm:min-w-[280px] sm:flex-1"
          maxTagCount="responsive"
          value={draft.tenants}
          onChange={(tenants) => setDraft({ ...draft, tenants })}
          options={tenantOptions.map((t) => ({ value: t.name, label: tenantOptionLabel(t) }))}
          filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        />
        <Tooltip title="Hides traffic with no tenant tag — client_name “unknown” or not set (mostly app pre-login screens). Untick to include it.">
          <Checkbox checked={draft.hideUntagged} onChange={(e) => setDraft({ ...draft, hideUntagged: e.target.checked })}>
            Hide untagged
          </Checkbox>
        </Tooltip>
        <RangePicker
          className="w-full sm:w-auto"
          value={draft.range}
          onChange={(v) => v && v[0] && v[1] && setDraft({ ...draft, range: v })}
          allowClear={false}
          presets={[
            { label: "Last 7 days", value: [dayjs().subtract(6, "day"), dayjs()] },
            { label: "Last 30 days", value: [dayjs().subtract(29, "day"), dayjs()] },
            { label: "Last 90 days", value: [dayjs().subtract(89, "day"), dayjs()] },
          ]}
        />
      </div>

      {renderGroup("Web", WEB_SEGMENTS)}
      {renderGroup("App", APP_SEGMENTS)}

      {showCompare && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="mini-label w-16">Compare</span>
          <Radio.Group
            size="small"
            optionType="button"
            value={draft.compareMode}
            onChange={(e) => setCompareMode(e.target.value)}
            options={[
              { label: "Previous period", value: "previous" },
              { label: "Custom start", value: "custom" },
            ]}
          />
          {draft.compareMode === "custom" && (
            <DatePicker
              value={draft.compareStart}
              allowClear={false}
              placeholder="Comparison start"
              onChange={(v) => v && setDraft({ ...draft, compareStart: v })}
              disabledDate={(d) => d.add(days - 1, "day").isAfter(dayjs(), "day")}
            />
          )}
          <span className="text-[12px] text-slate-600">
            {formatRange(draft.range)} <span className="text-slate-400">vs</span> {formatRange(compareRange)} · {days} day{days === 1 ? "" : "s"} each
          </span>
          {noAppDataEarlier && (
            <span className="text-[12px] text-amber-600">App tracking only ramped up from {APP_TRACKING_START.format("D MMM YYYY")} — app numbers before that are near zero, so app % changes will look inflated.</span>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[12px] text-slate-500">
          {summarize(value)}
          {dirty && <span className="text-amber-600"> · unapplied changes</span>}
        </span>
        <div className="flex items-center gap-2">
          <Tooltip title="Reset tenant and platform filters (keeps the date range)">
            <Button icon={<UndoOutlined />} onClick={reset}>
              Reset
            </Button>
          </Tooltip>
          <Button type="primary" disabled={!dirty} onClick={() => onApply(draft)}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
